import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ChevronLeft, Volume2, VolumeX, User, Paperclip, Image, FileText, Music, Video, Download, Loader2, Smile } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMOJIS_REACAO = ['👍', '❤️', '😄', '😮', '😢', '😡'];

const ChatWidget = () => {
  const { usuario, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [conversas, setConversas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [conversaAtual, setConversaAtual] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  const [somAtivo, setSomAtivo] = useState(() => {
    const saved = localStorage.getItem('chatSomAtivo');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [view, setView] = useState('conversas');
  const [typingUsers, setTypingUsers] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [reacaoMenuOpen, setReacaoMenuOpen] = useState(null);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(false);

  // Carregar som de notificação
  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleWw4R5q9z7+EdE5RmcTZxY51T1qWw9jIjnpUW5LB1seRfFldj7/UxZOAXWGMvdLDloRiZIq50cCZh2VnibfPvZyKaGqHtczAmYxrbIa0ysGcjm9whbLIv5+QcnODsMfBoJJ1d4KuxcKhlHh5ga3EwqKWeXqAq8LBopd7fH+qwMGjmH1+fqi/wKOZf4B+p76/pJmBgX6mvb6kmYKCfqS8vqWagYJ+pLu9ppqCgn6ju7ymm4KCfqK6u6ebgoJ+obi6p5yCgn6guLqnnIOCfp+3uaiegYJ+nra4qaCAg36dt7eqoYCDfZy2tqqhgIN9m7W1q6KAgn2btLSrooGCfZq0tKyjgYJ8mbOzrKOBgnyYsrKso4GCfJexsaykgYF8lrCwraWBgXuWr6+tpYGBe5SurqymgYB7lK2trqaAgHuTrayupoCAepKsrK6ngIB6kaurraeAgHmRqqquqH+AeZCpqa6of4B5j6ioq6l/f3mOp6erqX9/eI6mpqyqf394jaWlrKp/fneMpKSsq399d4ukpKysfn13i6OjrKx+fXaKoqKsrH59domhoaysfn12iaCgrK1+fHaIn5+srnx8doifn6yufHx1h56erq58fHWGnZ2urn18dYacnK6vfXx0hZubrq99fHSFmpqvsH18dISZma+wfXt0hJiYr7B9e3SDl5ewsX17c4OWlrCxfXtzgpWVsLJ9enOClJSwsn16coGTk7CzfnpygZKSsLN+enKBkZGwtH95coGQkLG0f3lxgI+PsbV/eXGAjo6xtX94cX+NjbG2gHhwfo2NsbZ/eHB+jIyxt4B4cH6Li7G3gHdwfYqKsbiAd29+iYmyuYB3b32IiLK5gHdvfYeHsrmBdm59hoayuoF2bn2FhbO6gXZufYSEs7uBdm18g4O0u4F1bXyCgrS7gnVtfIGBtLyDdW18gIC0vIN0bXx/f7S9g3RtfH5+tL2EdG18fX20voR0bHx8fLS+hXRsfHt7tL+FdGx7enq0v4Z0a3t5ebXAhnRre3h4tcCHc2t7d3e1wYdza3p2drXCiHNrenV1tsKIc2p5dHS2w4lyanl0c7bDinJqeHNztsOKcmp4cnK2xItyaXdxcbfEi3Jpdm9wt8WMcml2b2+3xYxyanVubrjFjXFpdG1tucaNcWh0bGy5x45xaHNra7nIjnFoc2pruciPcGdzamq6yY9wZ3JpabvJkHBmcmhousmQcGZxZ2e7ypBvZnFmZrvLkW9lcWVlvMuRb2VwZGS8zJJvZG9jY73Mkm5kb2JivcyTbmNuYWG9zZNuY25gYL7Nk21ibF9fv86UbWJsXl6/zpRtYWtdXb/PlWxha1xcwM+VbGBqW1vB0JZsYGpZWcHQlmtfaVhYwtGXa19pV1fC0ZdrX2hWVsPSmWteaFVVw9Kaa1xnVFTD05pqW2dTU8TUnGpbZ1JRxdSdallmUVDF1Z5qWGVQT8bVn2lXZE9OxtafalZkTk7H155pVWNNTcfYoGhUY0xMx9mgaFNiS0vI2aJoUmFKSsnarGdQYElJydqtZk9fSEjK26tnT14HR8vcxGZOXUZGy9zNZU1cRUXN3M5lTVtERM7dxWVMWkRDz93FZUtYQ0LQ3s5kS1hBQdDez2RKVkBA0d/OZEpVPz/R38tkSVQ+PtLgymNIUz0919/LY0ZROzvY38ljRVA5ONjgymJEUDc32eDLYkRPNjbb4MpiQ003N9vhy2FCS7Y22+HMYEJKtDXc4sxgQEixNd3jzV9BSas03uPNXz9KqTTe48xfPkilNOHkzF49Sacw4eTMXjxKpDDi5cxdPEqhL+Lmzlw7Sp4v5OfOWzpKnC/l58VbOUqcLubowls4TJou5ujCWjdMly7n6cJaN02ULuvo91k3TpIu7OiqWTZOky7t6qRZNU6TLu7rpFk1T5Mu7+ujWTVPky7w66JYNFGSLvHsolk0UZIu8u2gWDNRkS7z7Z9YM1KRLvTun1czUpEu9e+eVjNSkS72755WMlORLvfwnVYyU5Av+PGcVTJTkC/58ZtVMVOPL/nzmVQxVI8v+vOZVDBVjy/79JhTMFWPL/z1l1MwVo8u/PaWUi9WkC789pZSLlaQLv33lVEuV5Au/fiUUS1Xjy79+JRQLViQLv75k08tWI8u/vmTTyxZjy7++pJOLFmPLv77kU0sWY8u/vyRTStajy7//JBMK1qPLv//j0wrWo8u//+PSitagP/+');
  }, []);

  // Salvar preferência de som
  useEffect(() => {
    localStorage.setItem('chatSomAtivo', JSON.stringify(somAtivo));
  }, [somAtivo]);

  // Scroll para última mensagem
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [mensagens, scrollToBottom]);

  // Carregar conversas e mensagens não lidas
  const carregarDados = useCallback(async () => {
    try {
      const [conversasRes, usuariosRes, naoLidasRes] = await Promise.all([
        api.getConversas(),
        api.getChatUsuarios(),
        api.getMensagensNaoLidas()
      ]);
      setConversas(conversasRes.data);
      setUsuarios(usuariosRes.data);
      setTotalNaoLidas(naoLidasRes.data.total);
    } catch (error) {
      console.error('Erro ao carregar dados do chat:', error);
    }
  }, []);

  useEffect(() => {
    if (usuario && token) {
      carregarDados();
      const interval = setInterval(carregarDados, 30000);
      return () => clearInterval(interval);
    }
  }, [usuario, token, carregarDados]);

  // WebSocket connection
  useEffect(() => {
    if (!usuario || !token || !isOpen) return;

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/chat?token=${token}`);
    
    ws.onopen = () => {
      console.log('WebSocket conectado');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'nova_mensagem') {
        const msg = data.mensagem;
        
        if (somAtivo && audioRef.current) {
          audioRef.current.play().catch(() => {});
        }

        if (conversaAtual && msg.conversa_id === conversaAtual.id) {
          setMensagens(prev => [...prev, msg]);
        }
        
        carregarDados();
        toast.info(`Nova mensagem de ${msg.remetente_nome}`);
      }
      
      if (data.type === 'typing') {
        if (data.is_typing) {
          setTypingUsers(prev => ({
            ...prev,
            [data.user_id]: data.user_nome
          }));
          setTimeout(() => {
            setTypingUsers(prev => {
              const newState = { ...prev };
              delete newState[data.user_id];
              return newState;
            });
          }, 3000);
        } else {
          setTypingUsers(prev => {
            const newState = { ...prev };
            delete newState[data.user_id];
            return newState;
          });
        }
      }

      if (data.type === 'reacao') {
        // Atualizar reações da mensagem
        setMensagens(prev => prev.map(msg => 
          msg.id === data.mensagem_id 
            ? { ...msg, reacoes: data.reacoes }
            : msg
        ));
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket erro:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket desconectado');
    };

    wsRef.current = ws;

    return () => {
      ws.close();
    };
  }, [usuario, token, isOpen, somAtivo, conversaAtual, carregarDados]);

  // Enviar indicador de digitação
  const sendTypingIndicator = useCallback(async (isTyping) => {
    if (!conversaAtual?.participante?.id) return;
    
    try {
      await api.sendTypingIndicator({
        destinatario_id: conversaAtual.participante.id,
        is_typing: isTyping
      });
    } catch (error) {
      // Silenciosamente ignora erros de typing
    }
  }, [conversaAtual]);

  // Detectar digitação
  const handleInputChange = (e) => {
    setNovaMensagem(e.target.value);
    
    if (!lastTypingSentRef.current && e.target.value.length > 0) {
      lastTypingSentRef.current = true;
      sendTypingIndicator(true);
    }
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      lastTypingSentRef.current = false;
      sendTypingIndicator(false);
    }, 2000);
  };

  // Carregar mensagens da conversa
  const abrirConversa = async (conversa) => {
    setConversaAtual(conversa);
    setView('chat');
    setLoading(true);
    
    try {
      const response = await api.getMensagens(conversa.id);
      setMensagens(response.data);
      carregarDados();
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  // Iniciar nova conversa com usuário
  const iniciarConversa = (usuarioSelecionado) => {
    const conversaExistente = conversas.find(
      c => c.participante.id === usuarioSelecionado.id
    );
    
    if (conversaExistente) {
      abrirConversa(conversaExistente);
    } else {
      setConversaAtual({
        id: null,
        participante: {
          id: usuarioSelecionado.id,
          nome: usuarioSelecionado.nome,
          tipo: usuarioSelecionado.tipo
        }
      });
      setMensagens([]);
      setView('chat');
    }
  };

  // Upload de arquivo
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 10MB');
      return;
    }

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewFile({
          file,
          preview: e.target.result,
          type: 'imagem'
        });
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewFile({
        file,
        preview: null,
        type: file.type.startsWith('audio/') ? 'audio' : 
              file.type.startsWith('video/') ? 'video' : 'documento'
      });
    }
  };

  // Cancelar preview
  const cancelPreview = () => {
    setPreviewFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Enviar arquivo
  const handleSendFile = async () => {
    if (!previewFile || !conversaAtual) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', previewFile.file);
      
      const uploadRes = await api.uploadChatFile(formData);
      
      const response = await api.enviarMensagem({
        destinatario_id: conversaAtual.participante.id,
        conteudo: previewFile.file.name,
        tipo: uploadRes.data.tipo,
        arquivo_url: uploadRes.data.arquivo_url,
        arquivo_nome: uploadRes.data.arquivo_nome,
        arquivo_tipo: uploadRes.data.arquivo_tipo
      });
      
      setMensagens(prev => [...prev, response.data]);
      
      if (!conversaAtual.id) {
        setConversaAtual(prev => ({
          ...prev,
          id: response.data.conversa_id
        }));
      }
      
      carregarDados();
      cancelPreview();
    } catch (error) {
      toast.error('Erro ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  // Enviar mensagem de texto
  const handleEnviarMensagem = async (e) => {
    e.preventDefault();
    if (!novaMensagem.trim() || !conversaAtual) return;

    const conteudo = novaMensagem.trim();
    setNovaMensagem('');
    lastTypingSentRef.current = false;
    sendTypingIndicator(false);

    try {
      const response = await api.enviarMensagem({
        destinatario_id: conversaAtual.participante.id,
        conteudo,
        tipo: 'texto'
      });
      
      setMensagens(prev => [...prev, response.data]);
      
      if (!conversaAtual.id) {
        setConversaAtual(prev => ({
          ...prev,
          id: response.data.conversa_id
        }));
      }
      
      carregarDados();
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
      setNovaMensagem(conteudo);
    }
  };

  // Adicionar reação
  const handleAddReacao = async (mensagemId, emoji) => {
    try {
      const response = await api.adicionarReacao(mensagemId, emoji);
      setMensagens(prev => prev.map(msg => 
        msg.id === mensagemId 
          ? { ...msg, reacoes: response.data.reacoes }
          : msg
      ));
      setReacaoMenuOpen(null);
    } catch (error) {
      toast.error('Erro ao adicionar reação');
    }
  };

  // Voltar para lista
  const voltarParaLista = () => {
    setConversaAtual(null);
    setMensagens([]);
    setView('conversas');
    setPreviewFile(null);
  };

  const formatarHora = (timestamp) => {
    const date = new Date(timestamp);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);
    
    if (date.toDateString() === hoje.toDateString()) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === ontem.toDateString()) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };

  // Obter URL do arquivo com autenticação
  const getFileUrl = (arquivoUrl) => {
    if (!arquivoUrl) return '';
    const filename = arquivoUrl.split('/').pop();
    return `${BACKEND_URL}/api/chat/files/view/${filename}?token=${token}`;
  };

  // Renderizar reações
  const renderReacoes = (msg) => {
    const reacoes = msg.reacoes || {};
    const hasReacoes = Object.keys(reacoes).length > 0;
    
    if (!hasReacoes) return null;
    
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {Object.entries(reacoes).map(([emoji, users]) => (
          <button
            key={emoji}
            onClick={() => handleAddReacao(msg.id, emoji)}
            className={`text-xs px-1.5 py-0.5 rounded-full border transition-colors ${
              users.includes(usuario.id)
                ? 'bg-blue-100 border-blue-300'
                : 'bg-slate-100 border-slate-200 hover:bg-slate-200'
            }`}
            title={`${users.length} reação(ões)`}
          >
            {emoji} {users.length}
          </button>
        ))}
      </div>
    );
  };

  // Renderizar conteúdo da mensagem
  const renderMensagemContent = (msg) => {
    const isOwn = msg.remetente_id === usuario.id;
    
    if (msg.tipo === 'imagem' && msg.arquivo_url) {
      return (
        <div className="space-y-1">
          <img 
            src={getFileUrl(msg.arquivo_url)}
            alt={msg.arquivo_nome || 'Imagem'}
            className="max-w-full rounded-lg cursor-pointer hover:opacity-90"
            style={{ maxHeight: '200px' }}
            onClick={() => window.open(getFileUrl(msg.arquivo_url), '_blank')}
          />
          <p className={`text-xs ${isOwn ? 'text-slate-400' : 'text-slate-500'}`}>
            {formatarHora(msg.timestamp)}
          </p>
          {renderReacoes(msg)}
        </div>
      );
    }
    
    if (msg.tipo === 'audio' && msg.arquivo_url) {
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs truncate max-w-[150px]">{msg.arquivo_nome}</span>
          </div>
          <audio 
            controls 
            className="w-full max-w-[220px]"
            style={{ height: '32px' }}
          >
            <source src={getFileUrl(msg.arquivo_url)} type={msg.arquivo_tipo} />
          </audio>
          <p className={`text-xs ${isOwn ? 'text-slate-400' : 'text-slate-500'}`}>
            {formatarHora(msg.timestamp)}
          </p>
          {renderReacoes(msg)}
        </div>
      );
    }
    
    if (msg.tipo === 'video' && msg.arquivo_url) {
      return (
        <div className="space-y-1">
          <video 
            controls 
            className="max-w-full rounded-lg"
            style={{ maxHeight: '200px' }}
          >
            <source src={getFileUrl(msg.arquivo_url)} type={msg.arquivo_tipo} />
          </video>
          <p className={`text-xs ${isOwn ? 'text-slate-400' : 'text-slate-500'}`}>
            {formatarHora(msg.timestamp)}
          </p>
          {renderReacoes(msg)}
        </div>
      );
    }
    
    if (msg.tipo === 'documento' && msg.arquivo_url) {
      return (
        <div className="space-y-1">
          <a 
            href={getFileUrl(msg.arquivo_url)}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 p-2 rounded-lg ${
              isOwn ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-200 hover:bg-slate-300'
            } transition-colors`}
          >
            <FileText className="w-5 h-5 flex-shrink-0" />
            <span className="text-xs truncate max-w-[150px]">{msg.arquivo_nome}</span>
            <Download className="w-4 h-4 flex-shrink-0" />
          </a>
          <p className={`text-xs ${isOwn ? 'text-slate-400' : 'text-slate-500'}`}>
            {formatarHora(msg.timestamp)}
          </p>
          {renderReacoes(msg)}
        </div>
      );
    }
    
    // Mensagem de texto padrão
    return (
      <>
        <p className="text-sm break-words">{msg.conteudo}</p>
        <p className={`text-xs mt-1 ${isOwn ? 'text-slate-400' : 'text-slate-500'}`}>
          {formatarHora(msg.timestamp)}
        </p>
        {renderReacoes(msg)}
      </>
    );
  };

  // Verificar se alguém está digitando na conversa atual
  const isTypingInCurrentChat = conversaAtual && Object.keys(typingUsers).some(
    userId => userId === conversaAtual.participante?.id
  );

  if (!usuario) return null;

  return (
    <>
      {/* Input de arquivo oculto */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
      />

      {/* Botão flutuante */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 lg:bottom-6 right-6 z-50 w-14 h-14 bg-slate-900 hover:bg-slate-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-105"
        data-testid="chat-toggle-button"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6" />
            {totalNaoLidas > 0 && (
              <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
              </span>
            )}
          </>
        )}
      </button>

      {/* Janela do chat */}
      {isOpen && (
        <div 
          className="fixed bottom-36 lg:bottom-24 right-6 z-50 w-80 sm:w-96 h-[28rem] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="chat-widget"
        >
          {/* Header */}
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
            {view === 'chat' && conversaAtual ? (
              <>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={voltarParaLista}
                    className="text-white hover:bg-slate-800 p-1 h-auto"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div>
                    <p className="font-semibold text-sm">{conversaAtual.participante.nome}</p>
                    <p className="text-xs text-slate-400">
                      {isTypingInCurrentChat ? (
                        <span className="text-green-400">digitando...</span>
                      ) : (
                        conversaAtual.participante.tipo
                      )}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <h3 className="font-semibold">Chat Interno</h3>
            )}
            <button
              onClick={() => setSomAtivo(!somAtivo)}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              title={somAtivo ? 'Desativar som' : 'Ativar som'}
              data-testid="chat-sound-toggle"
            >
              {somAtivo ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          </div>

          {/* Conteúdo */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {view === 'conversas' && (
              <>
                <div className="flex border-b border-slate-200">
                  <button
                    onClick={() => setView('conversas')}
                    className="flex-1 py-2 text-sm font-medium text-slate-900 border-b-2 border-slate-900 transition-colors"
                  >
                    Conversas
                  </button>
                  <button
                    onClick={() => setView('usuarios')}
                    className="flex-1 py-2 text-sm font-medium text-slate-500 transition-colors"
                  >
                    Usuários
                  </button>
                </div>

                <ScrollArea className="flex-1">
                  {conversas.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>Nenhuma conversa ainda</p>
                      <p className="text-xs mt-1">Clique em "Usuários" para iniciar</p>
                    </div>
                  ) : (
                    conversas.map((conversa) => (
                      <button
                        key={conversa.id}
                        onClick={() => abrirConversa(conversa)}
                        className="w-full p-3 flex items-center gap-3 hover:bg-slate-50 border-b border-slate-100 text-left transition-colors"
                        data-testid={`conversa-${conversa.id}`}
                      >
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-sm text-slate-900 truncate">
                              {conversa.participante.nome}
                            </p>
                            {conversa.ultima_mensagem_timestamp && (
                              <span className="text-xs text-slate-500">
                                {formatarHora(conversa.ultima_mensagem_timestamp)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-slate-500 truncate">
                              {conversa.ultima_mensagem || 'Nenhuma mensagem'}
                            </p>
                            {conversa.nao_lidas > 0 && (
                              <span className="ml-2 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                                {conversa.nao_lidas}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </ScrollArea>
              </>
            )}

            {view === 'usuarios' && (
              <>
                <div className="flex border-b border-slate-200">
                  <button
                    onClick={() => setView('conversas')}
                    className="flex-1 py-2 text-sm font-medium text-slate-500 transition-colors"
                  >
                    Conversas
                  </button>
                  <button
                    onClick={() => setView('usuarios')}
                    className="flex-1 py-2 text-sm font-medium text-slate-900 border-b-2 border-slate-900 transition-colors"
                  >
                    Usuários
                  </button>
                </div>

                <ScrollArea className="flex-1">
                  {usuarios.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">
                      <User className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>Nenhum outro usuário</p>
                    </div>
                  ) : (
                    usuarios.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => iniciarConversa(user)}
                        className="w-full p-3 flex items-center gap-3 hover:bg-slate-50 border-b border-slate-100 text-left transition-colors"
                        data-testid={`usuario-${user.id}`}
                      >
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{user.nome}</p>
                          <p className="text-xs text-slate-500">{user.tipo}</p>
                        </div>
                      </button>
                    ))
                  )}
                </ScrollArea>
              </>
            )}

            {view === 'chat' && conversaAtual && (
              <>
                {/* Preview de arquivo */}
                {previewFile && (
                  <div className="p-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-slate-600">Arquivo selecionado</span>
                      <button onClick={cancelPreview} className="text-slate-400 hover:text-slate-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      {previewFile.type === 'imagem' && previewFile.preview ? (
                        <img src={previewFile.preview} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                      ) : (
                        <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                          {previewFile.type === 'audio' && <Music className="w-6 h-6 text-slate-500" />}
                          {previewFile.type === 'video' && <Video className="w-6 h-6 text-slate-500" />}
                          {previewFile.type === 'documento' && <FileText className="w-6 h-6 text-slate-500" />}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{previewFile.file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(previewFile.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={handleSendFile}
                        disabled={uploading}
                        className="bg-slate-900 hover:bg-slate-800"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Mensagens */}
                <ScrollArea className="flex-1 p-3">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full"></div>
                    </div>
                  ) : mensagens.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500 text-sm">
                      <MessageCircle className="w-12 h-12 mb-2 text-slate-300" />
                      <p>Inicie a conversa!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {mensagens.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.remetente_id === usuario.id ? 'justify-end' : 'justify-start'} group`}
                        >
                          <div className="relative">
                            <div
                              className={`max-w-[80%] px-3 py-2 rounded-2xl ${
                                msg.remetente_id === usuario.id
                                  ? 'bg-slate-900 text-white rounded-br-md'
                                  : 'bg-slate-100 text-slate-900 rounded-bl-md'
                              }`}
                              data-testid={`mensagem-${msg.id}`}
                            >
                              {renderMensagemContent(msg)}
                            </div>
                            
                            {/* Botão de reação */}
                            <Popover open={reacaoMenuOpen === msg.id} onOpenChange={(open) => setReacaoMenuOpen(open ? msg.id : null)}>
                              <PopoverTrigger asChild>
                                <button
                                  className={`absolute -bottom-2 ${msg.remetente_id === usuario.id ? 'left-0' : 'right-0'} opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-slate-200 rounded-full p-1 shadow-sm hover:bg-slate-50`}
                                >
                                  <Smile className="w-4 h-4 text-slate-500" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-2" side="top">
                                <div className="flex gap-1">
                                  {EMOJIS_REACAO.map((emoji) => (
                                    <button
                                      key={emoji}
                                      onClick={() => handleAddReacao(msg.id, emoji)}
                                      className="text-xl hover:scale-125 transition-transform p-1"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                      ))}
                      {isTypingInCurrentChat && (
                        <div className="flex justify-start">
                          <div className="bg-slate-100 text-slate-500 px-3 py-2 rounded-2xl rounded-bl-md">
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Input */}
                {!previewFile && (
                  <form onSubmit={handleEnviarMensagem} className="p-3 border-t border-slate-200">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-shrink-0"
                        data-testid="chat-attach-button"
                      >
                        <Paperclip className="w-5 h-5" />
                      </Button>
                      <Input
                        value={novaMensagem}
                        onChange={handleInputChange}
                        placeholder="Digite sua mensagem..."
                        className="flex-1"
                        data-testid="chat-input"
                      />
                      <Button 
                        type="submit" 
                        size="sm" 
                        disabled={!novaMensagem.trim()}
                        className="bg-slate-900 hover:bg-slate-800"
                        data-testid="chat-send-button"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
